import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useCatalog } from "@/lib/useCatalog";
import { money } from "@/lib/pricing";
import { ArrowUpRight, Zap, CalendarHeart, Star, Heart, ChevronRight } from "lucide-react";
import { FaInstagram, FaYoutube, FaTiktok } from "react-icons/fa";

const HERO = "https://customer-assets-cm19k8pv.emergentagent.net/job_sweet-queue-1/artifacts/1nbz2xub_ChatGPT%20Image%20Sep%2013%2C%202026%2C%2009_16_34%20PM.webp";
const WEDDING_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/job_sweet-queue-1/artifacts/zjpw1yek_Sunset%20Garden%20Wedding%20Terrace.webp";

export default function Home() {
  const navigate = useNavigate();
  const catalog = useCatalog();
  const [orderInfo, setOrderInfo] = useState(null);
  useEffect(() => { api.get("/ordering-info").then(({ data }) => setOrderInfo(data)).catch(() => {}); }, []);
  const nextDayOk = orderInfo ? orderInfo.next_day_available : true;
  const styles = (catalog?.styles || []).filter((s) => s.modes.includes("pre_order"));

  const cheapest = (s) => {
    const sizeGroup = s.groups.find((g) => g.id === "size");
    if (sizeGroup) return (Number(s.base_price) || 0) + Math.min(...sizeGroup.options.map((o) => o.price));
    return s.base_price;
  };

  const modes = [
    { key: "same_day", icon: Zap, title: "Same-Day", blurb: "Ready today" },
    { key: "next_day", icon: CalendarHeart, title: "Next-Day", blurb: "Ready tomorrow" },
    { key: "pre_order", icon: Star, title: "Pre-Order", blurb: "Pick a date" },
  ];

  return (
    <div className="App bg-sugar-bg">
      <Navbar />

      {/* HERO */}
      <section className="sugar-hero-bg px-5 pb-8 pt-7">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="flex items-center gap-2.5 text-[10px] font-bold uppercase tracking-[0.28em] text-sugar-ink2">
            <span className="h-px w-7 bg-sugar-ink2/50" /> Custom cakes ready to order
          </span>
          <h1 className="mt-4 font-display text-[2.6rem] font-semibold leading-[0.98] tracking-tight text-sugar-ink">
            Cakes made to be <span className="italic font-normal text-sugar-rose">obsessed</span> over.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-sugar-ink2">
            Ordering a cake shouldn't be so complicated… so I created a super easy system for you to place your order in a few taps.
          </p>
          <Button data-testid="hero-order-btn" onClick={() => navigate("/order")} className="mt-6 h-auto w-full rounded-sm bg-sugar-ink py-7 text-base font-bold uppercase tracking-[0.18em] text-sugar-bg hover:bg-sugar-pinkhover">
            Start your order <ArrowUpRight size={20} className="ml-2" />
          </Button>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="mt-5 overflow-hidden rounded-sm border border-sugar-border sugar-card-shadow">
            <img src={HERO} alt="A cake for every celebration" className="w-full object-cover" />
          </motion.div>
        </motion.div>

        {/* social follow */}
        <div className="mt-7 border-t border-sugar-border pt-6 text-center">
          <p className="text-sm font-semibold text-sugar-ink">Follow me on Instagram, YouTube & TikTok</p>
          <div className="mt-4 flex items-center justify-center gap-5">
            <a href="https://www.instagram.com/mysugarobsession" target="_blank" rel="noreferrer" data-testid="social-instagram" aria-label="Instagram" className="transition-transform hover:scale-110">
              <FaInstagram size={30} style={{ color: "#E1306C" }} />
            </a>
            <a href="https://www.youtube.com/@mysugarobsession" target="_blank" rel="noreferrer" data-testid="social-youtube" aria-label="YouTube" className="transition-transform hover:scale-110">
              <FaYoutube size={32} style={{ color: "#FF0000" }} />
            </a>
            <a href="https://www.tiktok.com/@mysugarobsession" target="_blank" rel="noreferrer" data-testid="social-tiktok" aria-label="TikTok" className="transition-transform hover:scale-110">
              <FaTiktok size={27} style={{ color: "#010101" }} />
            </a>
          </div>
          <a href="https://www.instagram.com/mysugarobsession" target="_blank" rel="noreferrer" data-testid="home-instagram" className="mt-3 inline-block font-display text-lg font-semibold text-sugar-ink transition-colors hover:text-sugar-rose">
            @mysugarobsession
          </a>
        </div>
      </section>

      {/* QUICK MODES */}
      <section className="px-5 py-8">
        <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-sugar-rose">Start your order</span>
        <h2 className="mt-1.5 font-display text-3xl font-bold text-sugar-ink">When do you need it?</h2>
        <div className="mt-5 grid grid-cols-3 gap-3">
          {modes.map((m, i) => {
            const accent = { same_day: "#C68B98", next_day: "#A9757F", pre_order: "#3B2A23" }[m.key] || "#3B2A23";
            const disabled = m.key === "next_day" && !nextDayOk;
            return (
              <motion.button
                key={m.key}
                data-testid={`home-mode-${m.key}`}
                disabled={disabled}
                onClick={() => { if (!disabled) navigate(`/order?mode=${m.key}`); }}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`group flex aspect-square flex-col items-center justify-center rounded-xl border-2 bg-sugar-card p-3 text-center transition-all duration-200 ${disabled ? "cursor-not-allowed opacity-45" : "hover:-translate-y-1 hover:shadow-xl"}`}
                style={{ borderColor: accent + "40" }}
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl text-white shadow-md" style={{ backgroundColor: accent }}>
                  <m.icon size={22} strokeWidth={2.4} />
                </span>
                <h3 className="mt-2.5 font-display text-base font-bold leading-tight text-sugar-ink">{m.title}</h3>
                <p className="mt-1 text-[9px] font-bold leading-tight" style={{ color: accent }}>{disabled ? "Order by 6 PM" : m.blurb}</p>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* COLLECTION */}
      <section className="px-5 pb-8">
        <div className="mb-5 border-b border-sugar-border pb-5">
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-sugar-rose">The Menu</span>
          <h2 className="mt-2 font-display text-6xl font-semibold lowercase leading-[0.95] text-sugar-ink">the <span className="italic font-normal text-sugar-rose">sugar</span> collection</h2>
          <p className="mt-1.5 text-sm text-sugar-ink2">made for your sweetest moments</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {styles.map((s, i) => (
            <motion.button
              key={s.id}
              data-testid={`home-style-${s.id}`}
              onClick={() => navigate(`/order?mode=pre_order&style=${s.id}`)}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
              className="group overflow-hidden rounded-sm border border-sugar-border bg-sugar-card text-left sugar-lift"
            >
              <div className="relative overflow-hidden">
                <img src={s.image} alt={s.name} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute right-1.5 top-1.5 rounded-sm bg-sugar-bg/95 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sugar-ink">from {money(cheapest(s))}</span>
              </div>
              <div className="p-2">
                <h3 className="font-display text-sm font-semibold leading-tight text-sugar-ink">{s.name}</h3>
              </div>
            </motion.button>
          ))}
        </div>
        <Button data-testid="home-browse-btn" onClick={() => navigate("/order")} variant="outline" className="mt-6 w-full rounded-sm border-sugar-ink py-6 text-xs font-bold uppercase tracking-[0.18em] text-sugar-ink hover:bg-sugar-ink hover:text-sugar-bg">
          Browse & customize
        </Button>
      </section>

      {/* WEDDING */}
      <section className="px-5 pb-10">
        <button
          data-testid="home-wedding-card"
          onClick={() => navigate("/wedding")}
          className="group relative block w-full overflow-hidden rounded-2xl border border-sugar-border text-left sugar-lift"
        >
          <img src={WEDDING_IMG} alt="Wedding cakes" className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-sugar-ink/85 via-sugar-ink/25 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em]"><Heart size={12} fill="#fff" /> Weddings</span>
            <h3 className="mt-1.5 font-display text-2xl font-bold leading-tight">Planning a wedding cake?</h3>
            <p className="mt-1 text-sm text-white/85">Send your inquiry — pricing includes cake, flowers, toppers & delivery.</p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-sugar-ink">Start your inquiry <ChevronRight size={14} /></span>
          </div>
        </button>
      </section>

      <Footer />
    </div>
  );
}
